import {
  Button,
  HStack,
  Spacer,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { isAdminDashboard } from 'packages/common-frontend/utils';
import { useSlugToName } from 'packages/dataPipeline/hooks/slugToNameContext';
import { useUploadContext } from 'packages/dataPipeline/hooks/UploadContext';
import { useNodeProgress } from 'packages/dataPipeline/hooks/useNodeProgress';
import { SectionHeader } from 'packages/dataPipeline/ui/components/SectionHeader';

const VerificationActionStatus = ({
  artifactId,
  creditFacilityId,
}: {
  artifactId: string;
  creditFacilityId: string;
}) => {
  const navigate = useNavigate();

  const uploadContext = useUploadContext();
  const nodeProgress = useNodeProgress({ artifactId });

  if (nodeProgress.complete < nodeProgress.total) {
    return (
      <Text>
        {nodeProgress.complete} of {nodeProgress.total} steps complete
      </Text>
    );
  }

  finleyAssert(
    uploadContext.completeRecord,
    'completeRecord missing in VerificationActionStatus',
  );

  const {
    reportingDate,
    fileVersion,
    meshDocumentId,
  } = uploadContext.completeRecord.meshRunRecord;
  const { artifactDocumentId } = DataPipeline.readMeshDocumentId(
    meshDocumentId,
  );
  const verificationLink = `/funding-requests/verifications/pipeline/${artifactDocumentId}/${creditFacilityId}/${reportingDate}/${fileVersion}`;

  // we may have already verified (approved or denied request)
  // when we have approved or denied the userInputNode will have status
  const approvalPending = !nodeProgress.status;

  return (
    <Button
      colorScheme="v2-aurora"
      variant="secondary"
      onClick={() => {
        if (!isAdminDashboard()) {
          navigate(verificationLink);
        }
      }}
    >
      {approvalPending ? 'Verify Funding Request' : 'View Funding Request'}
    </Button>
  );
};

const FundingRequestActionStatus = ({ artifactId }: { artifactId: string }) => {
  const navigate = useNavigate();
  const nodeProgress = useNodeProgress({ artifactId });
  if (!nodeProgress.status) {
    // node status is not available, show the progress of the steps.
    return (
      <Text>
        {nodeProgress.complete} of {nodeProgress.total + 1} steps complete
      </Text>
    );
  }

  if (nodeProgress.status === 'success') {
    // button that navigates to funding request page.
    return (
      <Button
        colorScheme="v2-aurora"
        variant="secondary"
        onClick={() => {
          if (!isAdminDashboard()) {
            // TODO: we should fetch the id for the funding request
            // and navigate to the specific funding request page.
            // like: /funding-requests/4c4dad20-8c4d-485b-b691-9bae17b000a5
            navigate('/funding-requests/');
          }
        }}
      >
        Submit Funding Request
      </Button>
    );
  }

  return <Text>status:{nodeProgress.status}</Text>;
};

const FundingRequestAction = ({
  fundingRequest,
}: {
  fundingRequest: DataPipeline.FundingRequest;
}) => {
  const { cfSlugToName } = useSlugToName();
  const textColor = useColorModeValue(colors.black[70], colors.white[700]);
  const backgroundColor = useColorModeValue(
    colors.background[50],
    colors.gray[700],
  );
  const borderColor = useColorModeValue(colors.gray[100], '#ffffff29');

  return (
    <>
      <SectionHeader
        title={`Funding Request for ${cfSlugToName(
          fundingRequest.creditFacilitySlug,
        )}`}
      />

      <VStack
        as="span"
        alignItems="flex-start"
        spacing={1}
        borderRadius="8px"
        padding="16px"
        border={`1px solid ${borderColor}`}
        bg={backgroundColor}
        width="100%"
      >
        <HStack width="100%" justifyContent="space-between">
          <Text
            as="span"
            color={textColor}
            fontSize="md"
            fontWeight="bold"
            lineHeight="1.2rem"
          >
            Funding Request
          </Text>
          <Spacer />
          {/* we need to show a "submit funding request" for typical flows, or "verify" link for the verification flow */}
          {fundingRequest.verificationArtifactId ? (
            <VerificationActionStatus
              artifactId={fundingRequest.verificationArtifactId}
              creditFacilityId={fundingRequest.creditFacilityId}
            />
          ) : (
            <FundingRequestActionStatus
              artifactId={fundingRequest.artifactId}
            />
          )}
        </HStack>
      </VStack>
    </>
  );
};

export const FundingRequestActions = (): React.ReactElement => {
  const uploadContext = useUploadContext();

  const fundingRequests =
    uploadContext.completeRecord?.meshDocument.fundingRequests || [];

  return (
    <>
      {fundingRequests.map((fundingRequest) => {
        return (
          <FundingRequestAction
            key={fundingRequest.artifactId}
            fundingRequest={fundingRequest}
          />
        );
      })}
    </>
  );
};
